import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import MuiTableHead from '@mui/material/TableHead';

import { DrawerHeader } from '@components/drawer-header';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUsers, updateUser, deleteUser } from '@service/user';
import { Box, Grid, Modal, Typography, useTheme } from '@mui/material';
import { Icon } from '@components/icon';
import { useState, useEffect, useCallback, useContext } from 'react';
import { TUserData } from '@models/user-data';
import { GlobalContext } from '../../context/userContext';
import * as S from './styles';

type UserData = {
  username: string;
  active?: boolean;
  roles?: string[];
};

export function Users() {
  const [users, setUsers] = useState<TUserData[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [row, setRow] = useState<TUserData>();
  const queryClient = useQueryClient();
  const { mainUser } = useContext(GlobalContext);

  const isAdmin = mainUser?.roles?.some?.((role: string) => ['ADMINISTRADOR', 'SUPERADMINISTRADOR'].includes(role));

  const usersQuery = useQuery({
    queryKey: ['getUsers'],
    queryFn: getUsers,
  });

  const statusMutation = useMutation<void, Error, UserData, unknown>({
    mutationFn: (data) => updateUser(data),
  });

  const deleteMutation = useMutation<void, Error, string, unknown>({
    mutationFn: (username) => deleteUser(username),
  });

  const handleModalAndRowOpen = useCallback((selectedRow: TUserData) => {
    setModalOpen(true);
    setRow(selectedRow);
  }, []);

  const handleChangeUserStatus = useCallback(async () => {
    try {
      await statusMutation.mutateAsync({
        username: row?.username as string,
        active: !row?.active,
      });

      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      setModalOpen(false);
    } catch (err) {
      setModalOpen(false);
    }
  }, [queryClient, row?.active, row?.username, statusMutation]);

  const handleDeleteUser = useCallback(async (username: string) => {
    if (!window.confirm(`Deseja realmente excluir o usuário ${username} do banco de dados? Essa ação não poderá ser desfeita.`)) return;
    try {
      await deleteMutation.mutateAsync(username);
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      alert("Usuário excluído com sucesso.");
    } catch (err) {
      alert("Erro ao excluir o usuário.");
    }
  }, [deleteMutation, queryClient]);

  const handleRoleChange = useCallback(async (username: string, newRole: string) => {
    try {
      // If the new role is 'user', we might want to send an empty array or just ['user'] depending on backend.
      // Based on previous analysis, 'admin' is the main role. Let's assume 'user' means no special role or explicit 'user'.
      // The requirement says "dropdown... choose the level". 
      // Current implementation shows "roles" which is an array.
      // Let's assume we toggle between ['admin'] and []. or ['admin'] and ['user'].
      // Implementation plan assumed 'admin' and 'user'. 
      // If the user selects 'admin', we send roles: ['admin'].
      // If they select 'user', we send roles: ['user'] or []. 
      // Let's stick to sending the selected value as a single item array for now.

      const roles = [newRole];

      await statusMutation.mutateAsync({
        username: username,
        roles: roles,
      });

      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
    } catch (err) {
      console.error(err);
    }
  }, [queryClient, statusMutation]);


  useEffect(() => {
    if (usersQuery.data?.data && Array.isArray(usersQuery.data.data)) {
      setUsers(usersQuery.data.data as TUserData[]);
    } else if (Array.isArray(usersQuery.data)) {
      setUsers(usersQuery.data as TUserData[]);
    } else if (usersQuery.data?.data?.data && Array.isArray(usersQuery.data.data.data)) {
      setUsers(usersQuery.data.data.data as TUserData[]);
    } else {
      console.log("No valid users data found in usersQuery.data:", usersQuery.data);
    }
  }, [usersQuery.data, mainUser]);

  const theme = useTheme();

  return (
    <DrawerHeader title="Usuários portal">
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, md: 3 },
              bgcolor: 'background.paper',
              borderLeft: `6px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              mb: 1
            }}
          >
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                CONTROLE DE ACESSO
              </Typography>
              <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Usuários do Portal</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Gerencie as credenciais de acesso ao sistema de telemetria. Atribua funções, defina níveis de permissão (Admin/User) e ative/desative contas para garantir a segurança dos dados operacionais.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Accordion sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 1 }}>
            <AccordionSummary expandIcon={<span style={{ fontSize: '1.5rem' }}>▼</span>}>
              <Typography variant="h6" fontFamily="Saira" fontWeight={600} color="primary">
                Tabela de Permissões e Níveis de Acesso
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <MuiTableHead>
                  <TableRow>
                    <TableCell><strong>Nível</strong></TableCell>
                    <TableCell><strong>Descrição</strong></TableCell>
                  </TableRow>
                </MuiTableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>VISUALIZACAO</strong></TableCell>
                    <TableCell>Acesso somente-leitura aos dashboards, checklists e relatórios de ocorrências/manutenção.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>ADICAO</strong></TableCell>
                    <TableCell>Inclui permissões de Visualização. Pode cadastrar novos equipamentos, importar planilhas e visualizar o painel de usuários de máquina.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>MODIFICAO</strong></TableCell>
                    <TableCell>Inclui permissões de Adição. Pode editar equipamentos, criar/editar cercas virtuais, configurações de checklist, atribuição de checklists, e usar o bloqueio remoto.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>ADMINISTRADOR</strong></TableCell>
                    <TableCell>Acesso total ao sistema. Pode gerenciar usuários do portal (incluindo atribuir papéis), visualizar o histórico de modificações, gerenciar TIDs, Firmware e configurações globais de operação.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>SUPERADMINISTRADOR</strong></TableCell>
                    <TableCell>Acesso irrestrito a todas as operações (Uso exclusivo da equipe interna da Automni).</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      {usersQuery.isLoading ? (
        <Grid container alignItems="center" justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }} aria-label="users table">
            <S.TableHead>
              <TableRow>
                <TableCell
                  style={{
                    borderBottom: '1px solid #a51033',
                    height: '70px',
                  }}
                />
                <S.TableCellHeader>Username</S.TableCellHeader>
                <S.TableCellHeader>Nome</S.TableCellHeader>
                <S.TableCellHeader>E-mail</S.TableCellHeader>
                <S.TableCellHeader>Empresa</S.TableCellHeader>
                <S.TableCellHeader>Função</S.TableCellHeader>
                <S.TableCellHeader>Níveis de Acesso</S.TableCellHeader>
                <S.TableCellHeader>Status</S.TableCellHeader>
              </TableRow>
            </S.TableHead>
            <TableBody>
              {users?.map((element: TUserData) => (
                <TableRow
                  key={element.email}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  <TableCell component="th" scope="row" />
                  <S.TableCell>{element.username}</S.TableCell>
                  <S.TableCell>{element.name}</S.TableCell>
                  <S.TableCell>{element.email}</S.TableCell>
                  <S.TableCell>{element.employed_by}</S.TableCell>
                  <S.TableCell>{element.function}</S.TableCell>
                  <S.TableCell>
                    {isAdmin ? (
                      <FormControl fullWidth variant="standard">
                        <Select
                          value={element.roles && element.roles.length > 0 ? element.roles[0] : 'VISUALIZACAO'}
                          onChange={(e) => handleRoleChange(element.username, e.target.value)}
                          disableUnderline
                        >
                          <MenuItem value="VISUALIZACAO">Visualização</MenuItem>
                          <MenuItem value="ADICAO">Adição</MenuItem>
                          <MenuItem value="MODIFICAO">Modificação</MenuItem>
                          <MenuItem value="ADMINISTRADOR">Administrador</MenuItem>
                          {mainUser?.roles?.includes('SUPERADMINISTRADOR') && (
                            <MenuItem value="SUPERADMINISTRADOR">Super Administrador (Automni)</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    ) : (
                      element.roles
                    )}
                  </S.TableCell>
                  <S.TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      {element.active === true ? (
                        <S.IconBox
                          onClick={() =>
                            handleModalAndRowOpen(element as TUserData)
                          }
                        >
                          <Icon disableTheming name="aprove" alt="aprove icon" width={36} />
                        </S.IconBox>
                      ) : (
                        <S.IconBox
                          onClick={() =>
                            handleModalAndRowOpen(element as TUserData)
                          }
                        >
                          <Icon disableTheming name="error" alt="error icon" width={36} />
                        </S.IconBox>
                      )}
                      
                      {isAdmin && (
                        <S.IconBox
                          onClick={() => handleDeleteUser(element.username)}
                        >
                          <Icon disableTheming name="trash" alt="delete icon" width={24} />
                        </S.IconBox>
                      )}
                    </Box>
                  </S.TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {modalOpen && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <S.ModalContainer>
            <Box
              maxWidth={200}
              justifyContent="center"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                variant="h5"
                color="primary"
                fontWeight={600}
                textAlign="center"
                mt={2}
              >
                Tem certeza que deseja alterar o status deste usuário?
              </Typography>
              <S.ButtonContainer>
                <S.ButtonYes onClick={() => handleChangeUserStatus()}>
                  SIM
                </S.ButtonYes>
                <S.ButtonNo onClick={() => setModalOpen(false)}>NÃO</S.ButtonNo>
              </S.ButtonContainer>
            </Box>
          </S.ModalContainer>
        </Modal>
      )}
    </DrawerHeader>
  );
}
